// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import type { Answer, ToolCall } from '../../../CopilotContext';

// Per-call display state. Derived from the persisted decision and execution; `running`
// is the one state with no persisted form - it is held locally while a run is in flight.
export type ToolCallState =
    | 'pending'
    | 'queued'
    | 'running'
    | 'succeeded'
    | 'failed-notfound'
    | 'failed-error'
    | 'rejected';

// The batch header glyph: a per-call state for a uniform batch, or `mixed`
export type BatchState = ToolCallState | 'mixed';

// Above this many characters a stored result is shown verbatim rather than pretty-printed
export const PRETTY_PRINT_MAX_CHARS = 64000;

type ToolCallFields = {
    id: string;
    messageId: string;
    type: string;
    name: string;
    arguments: string;
    title: string | null;
    label: string;
    readOnly: boolean;
    decision: ToolCall['decision'];
    execution: ToolCall['execution'];
    outcome: string | null;
    resultChars: number | null;
    storedChars: number | null;
    modelResultChars: number | null;
};

// A GraphQL CopilotToolCall selection as the client keeps it: the selection is the same
// in every query, so this only drops __typename
export const toToolCall = (call: ToolCallFields): ToolCall => ({
    id: call.id,
    messageId: call.messageId,
    type: call.type,
    name: call.name,
    arguments: call.arguments,
    title: call.title,
    label: call.label,
    readOnly: call.readOnly,
    decision: call.decision,
    execution: call.execution,
    outcome: call.outcome,
    resultChars: call.resultChars,
    storedChars: call.storedChars,
    modelResultChars: call.modelResultChars,
});

export const toToolCalls = (calls: readonly (ToolCallFields | null)[] | null | undefined): ToolCall[] | undefined => {
    if (!calls) {
        return undefined;
    }
    return calls.filter((call): call is ToolCallFields => call !== null).map(toToolCall);
};

export const isAutoDecision = (decision: ToolCall['decision']): boolean => decision === 'AUTO_READONLY' || decision === 'AUTO_YOLO';

export const isTruncated = (call: ToolCall): boolean => call.modelResultChars != null
    && call.resultChars != null
    && call.modelResultChars < call.resultChars;

export const isStoredCapped = (call: ToolCall): boolean => call.storedChars != null
    && call.resultChars != null
    && call.storedChars < call.resultChars;

export const hasExecutedResult = (call: ToolCall): boolean => call.execution === 'SUCCEEDED'
    || call.execution === 'FAILED_ERROR'
    || call.execution === 'FAILED_NOTFOUND';

export const toolCallState = (call: ToolCall, inFlight: boolean): ToolCallState => {
    // A rejection renders the same before and after its batch runs (execution SKIPPED)
    if (call.decision === 'REJECTED' || call.execution === 'SKIPPED') {
        return 'rejected';
    }
    switch (call.execution) {
        case 'SUCCEEDED':
            return 'succeeded';
        case 'FAILED_NOTFOUND':
            return 'failed-notfound';
        case 'FAILED_ERROR':
            return 'failed-error';
        default:
            break;
    }
    if (call.decision === null) {
        return 'pending';
    }
    // A run invokes every call still to run at once
    return inFlight ? 'running' : 'queued';
};

export const batchState = (calls: ToolCall[], inFlight: boolean): BatchState => {
    if (inFlight) {
        return 'running';
    }
    if (calls.some(call => call.decision === null)) {
        return 'pending';
    }
    if (calls.some(call => call.execution === null)) {
        return 'queued';
    }
    const executions = new Set(calls.map(call => call.execution));
    if (executions.size === 1) {
        switch (calls[0].execution) {
            case 'SUCCEEDED':
                return 'succeeded';
            case 'SKIPPED':
                return 'rejected';
            default:
                break;
        }
    }
    // Any all-failed batch is the muted circle-minus: the calls' own glyphs tell errors apart
    if ([...executions].every(execution => execution === 'FAILED_NOTFOUND' || execution === 'FAILED_ERROR')) {
        return 'failed-notfound';
    }
    return 'mixed';
};

export type BatchCounts = {
    total: number;
    succeeded: number;
    failed: number;
    rejected: number;
    allAuto: boolean;
};

export const batchCounts = (calls: ToolCall[]): BatchCounts => ({
    total: calls.length,
    succeeded: calls.filter(call => call.execution === 'SUCCEEDED').length,
    failed: calls.filter(call => call.execution === 'FAILED_ERROR' || call.execution === 'FAILED_NOTFOUND').length,
    rejected: calls.filter(call => call.execution === 'SKIPPED').length,
    allAuto: calls.length > 0 && calls.every(call => isAutoDecision(call.decision)),
});

/**
 * Monotonic merge of two copies of one call: a set decision or execution is never cleared,
 * and the result fields come from whichever side has executed. Responses to separate
 * requests can arrive in any order, and this makes the order irrelevant.
 */
export const mergeToolCall = (current: ToolCall, incoming: ToolCall): ToolCall => {
    const executed = incoming.execution !== null ? incoming : current;
    return {
        ...incoming,
        decision: incoming.decision ?? current.decision,
        execution: executed.execution,
        outcome: executed.outcome,
        resultChars: executed.resultChars,
        modelResultChars: executed.modelResultChars,
        storedChars: executed.storedChars,
    };
};

export const mergeToolCallList = (current: ToolCall[] | undefined, incoming: ToolCall[]): ToolCall[] => {
    const merged = (current ?? []).map(call => {
        const update = incoming.find(other => other.id === call.id);
        return update ? mergeToolCall(call, update) : call;
    });
    incoming.forEach(call => {
        if (!merged.some(other => other.id === call.id)) {
            merged.push(call);
        }
    });
    return merged;
};

/**
 * Merges calls into the answers that issued them, addressed by (messageId, id): wire ids
 * repeat across turns. Calls of an answer not in the list are dropped.
 */
export const mergeToolCallsIntoAnswers = (answers: Answer[], calls: ToolCall[]): Answer[] => {
    if (calls.length === 0) {
        return answers;
    }
    let changed = false;
    const next = answers.map(answer => {
        if (answer.id === null || answer.role !== 'assistant') {
            return answer;
        }
        const own = calls.filter(call => call.messageId === answer.id);
        if (own.length === 0) {
            return answer;
        }
        changed = true;
        return { ...answer, toolCalls: mergeToolCallList(answer.toolCalls, own) };
    });
    return changed ? next : answers;
};

/**
 * The conversation's latest persisted assistant row. An answer with no id - an error, or a
 * turn still streaming - is not a row.
 */
export const latestAssistantRow = (answers: Answer[]): Answer | undefined => {
    for (let i = answers.length - 1; i >= 0; i--) {
        if (answers[i].role === 'assistant' && answers[i].id !== null) {
            return answers[i];
        }
    }
    return undefined;
};

export type ComposerBlock = 'undecided' | 'unexecuted';

/**
 * Why a new message cannot be sent, if it cannot: the latest row's batch has a call not
 * executed yet. Undecided and decided-but-unexecuted calls block alike, since sending past
 * either would leave the history ending in tool calls without tool results.
 */
export const composerBlock = (answers: Answer[]): ComposerBlock | null => {
    const calls = latestAssistantRow(answers)?.toolCalls ?? [];
    if (!calls.some(call => call.execution === null)) {
        return null;
    }
    return calls.some(call => call.decision === null) ? 'undecided' : 'unexecuted';
};

/**
 * Whether the batch is waiting to be run: every call decided, at least one not executed.
 */
export const isAwaitingRun = (calls: ToolCall[]): boolean => calls.length > 0
    && calls.every(call => call.decision !== null)
    && calls.some(call => call.execution === null);

/**
 * Whether the batch stops once it has run, rather than the model being asked to continue:
 * the admin rejected a call of it and approved none. Automatic decisions are not approvals.
 * The server's rule; the client only uses it to tell a settled batch that stopped from one
 * whose continuation is still to come.
 */
export const stopsAfterRun = (calls: ToolCall[]): boolean => calls.some(call => call.decision === 'REJECTED')
    && !calls.some(call => call.decision === 'APPROVED');

// Pretty-prints JSON arguments; anything else is shown as passed
export const formatArguments = (args: string): string => {
    if (args.trim() === '') {
        return '{}';
    }
    try {
        return JSON.stringify(JSON.parse(args), null, 2);
    } catch {
        return args;
    }
};

// Pretty-prints a stored result when it is JSON and small enough; otherwise verbatim
export const formatResult = (result: string, resultChars: number | null): { text: string; formatted: boolean } => {
    if (resultChars != null && resultChars > PRETTY_PRINT_MAX_CHARS) {
        return { text: result, formatted: false };
    }
    try {
        return { text: JSON.stringify(JSON.parse(result), null, 2), formatted: true };
    } catch {
        return { text: result, formatted: false };
    }
};

export const uiLocale = (): string => document.documentElement.lang || 'en-US';

export const formatNumber = (value: number): string => new Intl.NumberFormat(uiLocale()).format(value);
